import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Loader2, ImageOff, LayoutGrid } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

// iter-387 — Catalog Preview. Strictly read-only: fetches the catalog's own
// saved items and renders a mock of the customer-facing page. Never creates
// an order, touches the cart, or writes any data — purely a vendor-side
// "how will this look" sanity check ahead of the real Phase 2 storefront.
const CATALOG_MODE_LABELS = {
  menu: 'Menu',
  bundle: 'Bundle',
  accommodation: 'Accommodation',
  package: 'Mixed Package',
};

export const CatalogPreviewModal = ({ open, onOpenChange, listingId, formData }) => {
  const { apiCall } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchItems = useCallback(async () => {
    if (!listingId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiCall(`/api/listings/${listingId}/catalog-items`);
      setItems(Array.isArray(res) ? res : []);
    } catch (err) {
      setError(err.message || 'Could not load catalog items for preview');
    } finally {
      setLoading(false);
    }
  }, [listingId, apiCall]);

  useEffect(() => {
    if (open) fetchItems();
  }, [open, fetchItems]);

  const coverImage = Array.isArray(formData?.media) && formData.media.length > 0
    ? (typeof formData.media[0] === 'string' ? formData.media[0] : formData.media[0]?.url)
    : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="catalog-preview-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LayoutGrid className="h-4 w-4" /> Catalog Preview
          </DialogTitle>
          <DialogDescription>
            A read-only mock of how customers will see this catalog. This does not affect the
            published listing, cart, or any orders.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {coverImage ? (
            <img src={coverImage} alt={formData?.title || 'Catalog'} className="w-full h-40 object-cover rounded-lg" data-testid="catalog-preview-cover-image" />
          ) : (
            <div className="w-full h-24 bg-gray-100 rounded-lg flex items-center justify-center text-gray-400" data-testid="catalog-preview-cover-placeholder">
              <ImageOff className="h-6 w-6" />
            </div>
          )}

          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-lg font-semibold" data-testid="catalog-preview-title">{formData?.title || 'Untitled Catalog'}</h3>
              {formData?.catalog_mode && (
                <Badge variant="outline" data-testid="catalog-preview-mode-badge">
                  {CATALOG_MODE_LABELS[formData.catalog_mode] || formData.catalog_mode}
                </Badge>
              )}
            </div>
            <p className="text-sm text-gray-600 mt-1" data-testid="catalog-preview-description">
              {formData?.description || 'No description yet.'}
            </p>
          </div>

          <div className="border-t pt-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">
              {items.length} item{items.length === 1 ? '' : 's'} in this catalog
            </h4>

            {loading ? (
              <div className="flex items-center justify-center py-8 text-gray-400" data-testid="catalog-preview-loading">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            ) : error ? (
              <p className="text-sm text-red-600" data-testid="catalog-preview-error">{error}</p>
            ) : items.length === 0 ? (
              <p className="text-sm text-gray-500" data-testid="catalog-preview-empty">
                No items added yet — add some in the Catalog Items section below, then preview again.
              </p>
            ) : (
              <div className="space-y-2">
                {items.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center gap-3 border rounded-lg p-2.5"
                    data-testid={`catalog-preview-item-${item.id}`}
                  >
                    {item.component_listing_image ? (
                      <img src={item.component_listing_image} alt="" className="w-12 h-12 object-cover rounded-md flex-shrink-0" />
                    ) : (
                      <div className="w-12 h-12 bg-gray-100 rounded-md flex items-center justify-center text-gray-400 flex-shrink-0">
                        <ImageOff className="h-4 w-4" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{item.component_listing_title || 'Untitled item'}</p>
                      {item.component_listing_status !== 'active' && (
                        <span className="text-xs text-amber-600" data-testid={`catalog-preview-unavailable-${item.id}`}>
                          Currently unavailable
                        </span>
                      )}
                    </div>
                    <div className="text-sm font-semibold text-gray-700 flex-shrink-0">
                      ₹{item.component_listing_price || 0}
                    </div>
                    <div className="text-xs text-gray-400 flex-shrink-0 w-16 text-right">
                      Qty: {item.default_quantity}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CatalogPreviewModal;
